import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Wrench, Power, Save, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';

/**
 * Admin → Settings → Maintenance Mode tab.
 * Toggle takes effect globally — non-admin users see a full-page takeover
 * served from MaintenancePage.js (driven by MaintenanceContext polling).
 */
export function MaintenanceTab() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({ enabled: false, message: '', eta: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/api/admin/maintenance');
      setData({
        enabled: !!d.enabled,
        message: d.message || '',
        eta: d.eta ? toLocalInput(d.eta) : '',
      });
    } catch (e) {
      toast.error('Failed to load maintenance status');
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const save = async (overrideEnabled) => {
    setSaving(true);
    try {
      const payload = {
        enabled: typeof overrideEnabled === 'boolean' ? overrideEnabled : data.enabled,
        message: data.message,
        eta: data.eta ? new Date(data.eta).toISOString() : null,
      };
      const { data: d } = await api.put('/api/admin/maintenance', payload);
      setData({
        enabled: !!d.enabled,
        message: d.message || '',
        eta: d.eta ? toLocalInput(d.eta) : '',
      });
      toast.success(
        payload.enabled
          ? 'Maintenance mode is now ON — members will see the takeover screen'
          : 'Maintenance mode is now OFF — site is back to normal'
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <Card className="bg-[#041A3B] border-white/10">
        <CardContent className="p-10 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[#041A3B] border-white/10" data-testid="maintenance-tab">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Wrench className="w-5 h-5 text-[#00E5FF]" />
          Maintenance Mode
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Status banner */}
        <div className={`p-4 rounded-lg border ${
          data.enabled
            ? 'bg-amber-500/10 border-amber-500/30'
            : 'bg-[#00E676]/10 border-[#00E676]/30'
        }`}>
          <div className="flex items-center gap-3">
            {data.enabled
              ? <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
              : <CheckCircle className="w-5 h-5 text-[#00E676] flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm">
                {data.enabled ? 'Maintenance is ACTIVE' : 'Site is fully operational'}
              </p>
              <p className="text-[#A0B4CC] text-xs mt-0.5">
                {data.enabled
                  ? 'Members, merchants, and MROs are seeing the maintenance screen. Only admins can use the site.'
                  : 'All users have full access. Toggle ON to block non-admin traffic while you work.'}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => save(!data.enabled)}
              disabled={saving}
              className={
                data.enabled
                  ? 'bg-[#00E676] text-[#000B18] hover:bg-[#00E676]/90'
                  : 'bg-amber-500 text-[#000B18] hover:bg-amber-500/90'
              }
              data-testid="maintenance-toggle-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <><Power className="w-4 h-4 mr-1.5" /> {data.enabled ? 'Turn OFF' : 'Turn ON'}</>
              )}
            </Button>
          </div>
        </div>

        {/* Message */}
        <div>
          <Label className="text-[#A0B4CC] text-xs">Public message (shown to members)</Label>
          <textarea
            value={data.message}
            onChange={(e) => setData({ ...data, message: e.target.value })}
            rows={3}
            className="w-full mt-1 px-3 py-2 rounded-md bg-[#000B18] border border-white/20 text-white text-sm focus:border-[#00E5FF] focus:outline-none resize-none"
            placeholder="We're making things better. We'll be back shortly."
            data-testid="maintenance-message"
          />
        </div>

        {/* ETA */}
        <div>
          <Label className="text-[#A0B4CC] text-xs">Estimated end time (optional)</Label>
          <Input
            type="datetime-local"
            value={data.eta}
            onChange={(e) => setData({ ...data, eta: e.target.value })}
            className="bg-[#000B18] border-white/20 text-white mt-1 max-w-xs"
            data-testid="maintenance-eta"
          />
          <p className="text-[10px] text-[#5C7599] mt-1">
            Members will see this on the maintenance screen as &quot;back online by [time]&quot;.
          </p>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={() => save()}
            disabled={saving}
            className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90"
            data-testid="maintenance-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save changes</>}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Hard-delete all "test users" matched by email pattern.
 * 2-step flow with dry-run preview and typed confirmation guarding deletion.
 */
export function TestUsersCleanupCard() {
  const { api } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);

  const runPreview = async () => {
    setLoading(true);
    setConfirmation('');
    try {
      const { data } = await api.post('/api/admin/maintenance/test-users/preview');
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    }
    setLoading(false);
  };

  const expected = preview ? `DELETE ${preview.matched_count} USERS` : '';
  const canDelete = preview && preview.matched_count > 0 && confirmation === expected;

  const confirmDelete = async () => {
    if (!canDelete) return;
    setDeleting(true);
    try {
      const { data } = await api.post('/api/admin/maintenance/test-users/delete', {
        dry_run: false, confirmation_text: confirmation,
      });
      toast.success(`Hard-deleted ${data.deleted_count} test users (audit-logged).`);
      setPreview(null);
      setConfirmation('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Deletion failed');
    }
    setDeleting(false);
  };

  return (
    <Card className="bg-[#041A3B] border-red-500/20 mt-4" data-testid="test-users-cleanup-card">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          Delete Test Users
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-[#A0B4CC] text-sm">
          Hard-deletes all non-admin accounts whose email matches a test pattern
          (e.g., <code className="text-[#00E5FF]">@example.com</code>,{' '}
          <code className="text-[#00E5FF]">test_*@allglobalaccess.com</code>,
          <code className="text-[#00E5FF]">smoke_*</code>, etc.).
          Cascade-deletes their wallets, referral commissions, tickets, sessions, etc.
          Always audit-logged. <b className="text-red-300">Cannot be undone.</b>
        </p>

        {!preview && (
          <Button
            onClick={runPreview}
            disabled={loading}
            variant="outline"
            className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 hover:text-amber-200"
            data-testid="preview-test-users-btn"
          >
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning…</>
              : <>Preview Matched Test Users</>}
          </Button>
        )}

        {preview && (
          <div className="space-y-3" data-testid="test-users-preview">
            <div className="p-3 rounded-lg bg-[#000B18] border border-white/10">
              <p className="text-sm">
                <b className={preview.matched_count > 0 ? 'text-red-400' : 'text-[#00E676]'}>
                  {preview.matched_count}
                </b>
                {' '}users matched
                {preview.matched_count === 0 && ' — nothing to delete.'}
              </p>
              {preview.matched_count > 0 && (
                <div className="mt-2 max-h-60 overflow-y-auto mobile-scroll-wrap text-xs">
                  <table className="w-full text-left">
                    <thead className="text-[#5C7599] sticky top-0 bg-[#000B18]">
                      <tr><th className="p-1.5">Email</th><th className="p-1.5">Name</th><th className="p-1.5">Role</th></tr>
                    </thead>
                    <tbody>
                      {preview.users.map((u) => (
                        <tr key={u.id} className="border-t border-white/5 text-[#A0B4CC]">
                          <td className="p-1.5 break-all">{u.email}</td>
                          <td className="p-1.5">{u.name || '(no name)'}</td>
                          <td className="p-1.5 font-mono">{u.role}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {preview.matched_count > 0 && (
              <>
                <div>
                  <Label className="text-[#A0B4CC] text-xs mb-1.5 block">
                    Type{' '}
                    <code className="text-red-300 font-mono">{expected}</code>{' '}
                    exactly to confirm deletion:
                  </Label>
                  <Input
                    value={confirmation}
                    onChange={(e) => setConfirmation(e.target.value)}
                    placeholder={expected}
                    className="bg-[#000B18] border-white/20 text-white"
                    data-testid="delete-test-users-confirmation-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => { setPreview(null); setConfirmation(''); }}
                    variant="ghost"
                    className="text-[#A0B4CC]"
                    data-testid="cancel-test-users-delete-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={confirmDelete}
                    disabled={!canDelete || deleting}
                    className="bg-red-500/90 text-white hover:bg-red-500 disabled:opacity-40"
                    data-testid="confirm-test-users-delete-btn"
                  >
                    {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting…</>
                      : 'Permanently Delete'}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function toLocalInput(iso) {
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
}

export default MaintenanceTab;
